import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ToastContext } from './ToastContext.js';
import ToastViewport from './ToastViewport.jsx';

const randomId = () => Math.random().toString(36).slice(2);

const POSITION_CLASSES = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

export const ToastProvider = ({ children, config }) => {
  const [items, setItems] = useState([]);
  const [position, setPosition] = useState(config?.position || 'top-right');
  const [defaultMaxWidth, setDefaultMaxWidth] = useState(
    config?.defaultMaxWidth ?? '692px',
  );
  const [defaultMinWidth, setDefaultMinWidth] = useState(
    config?.defaultMinWidth ?? '320px',
  );
  const maxRef = useRef(config?.max || 4);
  const defaultDurationRef = useRef(config?.defaultDuration || 4500);
  const timersRef = useRef(new Map());

  useEffect(() => {
    if (config) {
      if (config.position) setPosition(config.position);
      if (Number.isFinite(config.max)) maxRef.current = config.max;
      if (Number.isFinite(config.defaultDuration))
        defaultDurationRef.current = config.defaultDuration;
      if (config.defaultMaxWidth != null) setDefaultMaxWidth(config.defaultMaxWidth);
      if (config.defaultMinWidth != null) setDefaultMinWidth(config.defaultMinWidth);
    }
  }, [config]);

  const push = useCallback((partial) => {
    const _id = partial.id || randomId();
    const duration =
      partial.duration != null ? partial.duration : defaultDurationRef.current;
    const canClose = partial.canClose == null ? false : partial.canClose;
    const item = {
      id: _id,
      createdAt: Date.now(),
      ...partial,
      canClose,
      duration,
    };

    let foundItem = null;
    setItems((prev) => {
      const dup = prev.find(
        (it) =>
          it.variant === item.variant &&
          it.message === item.message &&
          Date.now() - it.createdAt < 2000,
      );
      if (dup) {
        foundItem = dup;
        return prev;
      }

      const newItems = [item, ...prev];
      if (newItems.length > maxRef.current) {
        return newItems.slice(0, maxRef.current);
      }
      return newItems;
    });

    if (foundItem) return foundItem.id;

    if (duration > 0) {
      const t = window.setTimeout(() => {
        setItems((prev) => {
          const itemToClose = prev.find((x) => x.id === _id);
          itemToClose?.onClose?.();
          return prev.filter((x) => x.id !== _id);
        });
        timersRef.current.delete(_id);
      }, duration);
      timersRef.current.set(_id, t);
    }

    return _id;
  }, []);

  const close = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      window.clearTimeout(t);
      timersRef.current.delete(id);
    }
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      it?.onClose?.();
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setItems((prev) => {
      prev.forEach((it) => it.onClose?.());
      return [];
    });
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current.clear();
  }, []);

  const configure = useCallback((opts = {}) => {
    if (opts.position) setPosition(opts.position);
    if (Number.isFinite(opts.max)) maxRef.current = opts.max;
    if (Number.isFinite(opts.defaultDuration))
      defaultDurationRef.current = opts.defaultDuration;
    if (opts.defaultMaxWidth != null) setDefaultMaxWidth(opts.defaultMaxWidth);
    if (opts.defaultMinWidth != null) setDefaultMinWidth(opts.defaultMinWidth);
  }, []);

  const toastApi = useMemo(
    () => ({
      items,
      position,
      success: (opts) => push({ variant: 'success', ...opts }),
      info: (opts) => push({ variant: 'info', ...opts }),
      warning: (opts) => push({ variant: 'warning', ...opts }),
      error: (opts) => push({ variant: 'error', ...opts }),
      neutral: (opts) => push({ variant: 'neutral', ...opts }),
      clear,
      close,
      configure,
    }),
    [items, position, push, clear, close, configure],
  );

  const positionClass = POSITION_CLASSES[position] || POSITION_CLASSES['top-right'];

  return (
    <ToastContext.Provider value={toastApi}>
      <ToastViewport
        items={items}
        position={positionClass}
        onClose={close}
        defaultMaxWidth={defaultMaxWidth}
        defaultMinWidth={defaultMinWidth}
      />
      {children}
    </ToastContext.Provider>
  );
};
